import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { format, addDays, isToday, isTomorrow, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useAuthStore } from '@/store/authStore'
import { getTrainerBookings, Booking } from '@/services/bookings'
import api from '@/services/api'
import Avatar from '@/components/ui/Avatar'

interface PendingClient {
  id: string
  user: {
    first_name: string
    username?: string
    photo_url?: string
  }
  status: string
}

interface BookingsByDay {
  date: string
  label: string
  bookings: Booking[]
}

// Функция для группировки бронирований по дням
function groupBookingsByDay(bookings: Booking[]): BookingsByDay[] {
  const grouped: Record<string, Booking[]> = {}

  bookings.forEach((booking) => {
    const date = booking.slot.date
    if (!grouped[date]) {
      grouped[date] = []
    }
    grouped[date].push(booking)
  })

  // Сортируем по дате и формируем результат
  return Object.keys(grouped)
    .sort()
    .map((date) => {
      const parsedDate = parseISO(date)
      let label: string

      if (isToday(parsedDate)) {
        label = `Сегодня, ${format(parsedDate, 'd MMMM', { locale: ru })}`
      } else if (isTomorrow(parsedDate)) {
        label = `Завтра, ${format(parsedDate, 'd MMMM', { locale: ru })}`
      } else {
        label = format(parsedDate, 'EEEE, d MMMM', { locale: ru })
        // Capitalize first letter
        label = label.charAt(0).toUpperCase() + label.slice(1)
      }

      return {
        date,
        label,
        bookings: grouped[date].sort((a, b) =>
          a.slot.start_time.localeCompare(b.slot.start_time)
        ),
      }
    })
}

export default function TrainerDashboard() {
  const { user } = useAuthStore()
  const [upcomingBookings, setUpcomingBookings] = useState<BookingsByDay[]>([])
  const [pendingClients, setPendingClients] = useState<PendingClient[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        const today = new Date()
        const weekLater = addDays(today, 7)

        // Загружаем бронирования на 7 дней
        const bookings = await getTrainerBookings(
          format(today, 'yyyy-MM-dd'),
          format(weekLater, 'yyyy-MM-dd')
        )
        const confirmedBookings = bookings.filter((b) => b.status === 'confirmed')

        // Группируем по дням
        const groupedByDay = groupBookingsByDay(confirmedBookings)
        setUpcomingBookings(groupedByDay)

        // Загружаем ожидающих клиентов
        const pendingResponse = await api.get<{ pending: PendingClient[] }>(
          '/trainers/me/clients/pending'
        )
        setPendingClients(pendingResponse.data.pending || [])
      } catch (err) {
        console.error('Error loading dashboard data:', err)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [])

  // Общее количество тренировок
  const totalBookings = upcomingBookings.reduce(
    (sum, day) => sum + day.bookings.length,
    0
  )

  return (
    <div className="p-4">
      {/* Приветствие */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-tg-text">
          Привет, {user?.first_name}! 👋
        </h1>
        <p className="text-tg-hint mt-1">Ваша панель управления</p>
      </div>

      {/* Быстрые действия */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <QuickAction
          icon="📅"
          title="Расписание"
          description="Управление слотами"
          to="/schedule"
        />
        <QuickAction
          icon="👥"
          title="Клиенты"
          description="Список подопечных"
          to="/clients"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-tg-hint">Загрузка...</div>
      ) : (
        <>
          {/* Ближайшие тренировки */}
          <section className="mb-6">
            <h2 className="text-lg font-semibold text-tg-text mb-3">
              Ближайшие тренировки
              {totalBookings > 0 && (
                <span className="text-tg-hint font-normal text-sm ml-2">
                  ({totalBookings})
                </span>
              )}
            </h2>

            {upcomingBookings.length > 0 ? (
              <div className="space-y-4">
                {upcomingBookings.map((day) => (
                  <div key={day.date}>
                    {/* Заголовок дня */}
                    <div className="text-sm font-medium text-tg-hint mb-2">
                      {day.label}
                    </div>
                    {/* Тренировки за день */}
                    <div className="space-y-2">
                      {day.bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="bg-tg-secondary rounded-xl p-3 flex items-center gap-3"
                        >
                          <div className="text-xl">🏋️</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-tg-text">
                              {booking.slot.start_time} - {booking.slot.end_time}
                            </div>
                            <div className="text-sm text-tg-hint truncate">
                              {booking.guest_details ? `Гость: ${booking.guest_details}` : booking.client?.name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-tg-secondary rounded-xl p-4">
                <p className="text-tg-hint text-center py-4">
                  Нет тренировок на ближайшие 7 дней
                </p>
              </div>
            )}
          </section>

          {/* Ожидающие подтверждения */}
          <section>
            <h2 className="text-lg font-semibold text-tg-text mb-3">
              Новые заявки
              {pendingClients.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingClients.length}
                </span>
              )}
            </h2>
            {pendingClients.length > 0 ? (
              <div className="space-y-3">
                {pendingClients.map((client) => (
                  <div
                    key={client.id}
                    className="bg-tg-secondary border-l-4 border-orange-400 rounded-xl p-4 flex items-center gap-4"
                  >
                    <Avatar
                      src={client.user.photo_url}
                      name={client.user.first_name}
                      size="md"
                      fallbackEmoji="👤"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-tg-text">
                        {client.user.first_name}
                      </div>
                      {client.user.username && (
                        <div className="text-sm text-tg-hint">
                          @{client.user.username}
                        </div>
                      )}
                    </div>
                    <span className="text-xs bg-orange-400/20 text-orange-500 px-2 py-1 rounded-full font-medium">
                      Новая
                    </span>
                  </div>
                ))}
                <Link
                  to="/clients"
                  className="block text-center text-sm text-tg-button font-medium py-2"
                >
                  Управление заявками →
                </Link>
              </div>
            ) : (
              <div className="bg-tg-secondary rounded-xl p-4">
                <p className="text-tg-hint text-center py-4">
                  Нет новых заявок
                </p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

// Компонент быстрого действия
function QuickAction({
  icon,
  title,
  description,
  to,
}: {
  icon: string
  title: string
  description: string
  to: string
}) {
  return (
    <Link
      to={to}
      className="bg-tg-secondary rounded-xl p-4 block hover:opacity-80 transition-opacity"
    >
      <div className="text-3xl mb-2">{icon}</div>
      <div className="font-medium text-tg-text">{title}</div>
      <div className="text-xs text-tg-hint">{description}</div>
    </Link>
  )
}
